import { useAppDispatch, useAppSelector } from "@app/hooks"
import { ConsultantClient } from "@clients/consultantClient"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import {
  salesTeamWithShiftsSelector,
  onlineConsultantsSelector,
  selectSalesTeams,
} from "@features/sales-management/salesManagement.selectors"
import {
  fetchSalesTeams,
  fetchShifts,
  archiveConsultant,
  updateConsultantAvailability,
  updateConsultants,
  updateConsultantTags,
} from "@features/sales-management/salesManagement.slice"
import { CreateConsultantDto } from "@masterschool/course-builder-api"
import PlusIcon from "@mui/icons-material/Add"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import appIcons from "@utils/appIcons"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import EmptyStateContainer from "../programs/program/components/emptyStateContainer"
import appTheme from "../theme/appTheme"
import { ConsultantCell } from "./consultantCell"
import { TagsCell } from "./tagsCell"
import { TagManagerDialog } from "./tagManagerDialog"
import ContactForm from "./consultantForm"
import { ShiftCell } from "./shiftCell"
import { useNavigate } from "react-router-dom"
import { OnlineStatusUpdater } from "./Models/onlineStatusUpdater"
import { Role } from "@features/login/loginSlice"
import { showSnackbarItem, SnackbarDuration } from "@features/ui/uiSlice"
import OptionsButton2 from "@cmp/buttons/optionsButton2"
import { IconTextMenuItem } from "../main/courses-table/course-table/coursesCells"
import { AppDispatch } from "@app/store"

const EmptyState = (props: { onAddConsultantClick: () => void }) => {
  return (
    <EmptyStateContainer
      title="No consultants yet"
      subtitle="Add and manage consultants"
      icon={appIcons.bank}
      action={
        <Button variant="contained" onClick={props.onAddConsultantClick}>
          Add consultant
        </Button>
      }
    />
  )
}

export const SalesManagement = () => {
  const navigate = useNavigate()
  const consultantsTeams = useSelector(salesTeamWithShiftsSelector)

  // One flat list of consultants. Team is no longer a field of its own — the
  // migration folded every team name into consultant tags, so a team is just a
  // tag now (filter by it in the facet bar like any other).
  const consultants = useMemo(() => {
    if (!Array.isArray(consultantsTeams)) {
      return []
    }
    return consultantsTeams.flatMap((team) => team.consultants)
  }, [consultantsTeams])

  // Suggestions for the tags editor and the filter: every tag already in use.
  // Tag options come from the catalog, so a tag shows in the dropdown even when
  // no one carries it yet (freshly created, or everyone untagged).
  const [allTags, setAllTags] = useState<string[]>([])
  const loadCatalog = useCallback(() => {
    ConsultantClient.listTagCounts()
      .then((rows) => setAllTags(rows.map((row) => row.tag).sort()))
      .catch(() => setAllTags([]))
  }, [])
  useEffect(() => {
    loadCatalog()
  }, [loadCatalog])

  // Every facet is a catalog tag (teams included, since they're tags now).
  const allFacets = allTags

  const roles = useSelector(selectLoggedInUser)?.roles
  const isLMSAdmin = !!roles?.includes(Role.LmsAdmin)
  const [openConsultantInsertForm, setOpenConsultantInsertForm] =
    useState(false)
  const [manageTagsOpen, setManageTagsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [activeTags, setActiveTags] = useState<string[]>([])

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchSalesTeams())
  }, [dispatch])

  const salesTeams = useAppSelector(selectSalesTeams)
  useEffect(() => {
    dispatch(fetchShifts())
  }, [salesTeams, dispatch])

  useEffect(() => {
    const onlineStatusUpdater = new OnlineStatusUpdater(1)
    onlineStatusUpdater.start()
    return () => {
      onlineStatusUpdater.stop()
    }
  }, [])
  const onlineConsultants = useSelector(onlineConsultantsSelector)
  const onlineConsultansIds =
    onlineConsultants !== "pending"
      ? onlineConsultants.map((consultant) => consultant.id)
      : []

  const onCreateConsultant = (createConsultantModel: CreateConsultantDto) => {
    ConsultantClient.create(createConsultantModel)
      .then(() => {
        setOpenConsultantInsertForm(false)
        dispatch(updateConsultants())
      })
      .catch(() => {
        dispatch(
          showSnackbarItem({
            message:
              "Failed to create consultant, please verify that the email matches Hubspot consultant email",
            type: "error",
            duration: SnackbarDuration.short,
          }),
        )
      })
  }

  const handleAvailableChange = (params: {
    consultantId: string
    newIsAvailable: boolean
  }) => {
    dispatch(
      updateConsultantAvailability({
        consultantId: params.consultantId,
        isAvailable: params.newIsAvailable,
      }),
    )
  }

  const handleTagsChange = (consultantId: string, tags: string[]) => {
    dispatch(updateConsultantTags({ consultantId, tags })).then(() =>
      loadCatalog(),
    )
  }

  const toggleTag = (tag: string) =>
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    )

  // A consultant is shown when it matches the search and carries every active
  // filter tag (AND), so filters narrow, matching the routing page's facets.
  const filtered = consultants.filter((consultant) => {
    const consultantTags =
      (consultant as unknown as { tags?: string[] }).tags ?? []
    const matchesTags = activeTags.every((facet) =>
      consultantTags.includes(facet),
    )
    const needle = search.trim().toLowerCase()
    const matchesSearch =
      needle.length === 0 ||
      consultant.name?.toLowerCase().includes(needle) ||
      consultant.email?.toLowerCase().includes(needle)
    return matchesTags && matchesSearch
  })

  const renderEmptyState =
    consultantsTeams === "pending" ? (
      <Box />
    ) : (
      <EmptyState
        onAddConsultantClick={() => setOpenConsultantInsertForm(true)}
      />
    )
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="start"
      alignItems="center"
      height="100%"
      px="24px"
      pb="30px"
      width="100%"
      gap="16px"
    >
      <Box
        width="100%"
        paddingX="32px"
        display="flex"
        paddingTop="84px"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4">Sales management</Typography>
        <Box display="flex" gap="8px">
          <Button
            onClick={() => setManageTagsOpen(true)}
            variant="outlined"
            size="small"
          >
            <Typography variant="body2">Manage tags</Typography>
          </Button>
          {!!roles && roles.includes(Role.SalesTeamLead) && (
            <Button
              onClick={() => setOpenConsultantInsertForm(true)}
              variant="contained"
              size="small"
              startIcon={<PlusIcon />}
            >
              <Typography variant="body2" color="white">
                Add consultant
              </Typography>
            </Button>
          )}
        </Box>
      </Box>
      {consultantsTeams === "pending" || consultants.length === 0 ? (
        renderEmptyState
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          overflow="hidden"
          paddingX="30px"
          width="100%"
          gap="12px"
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              size="small"
              placeholder="Search name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {allFacets.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant={activeTags.includes(tag) ? "filled" : "outlined"}
                  color={activeTags.includes(tag) ? "primary" : "default"}
                  onClick={() => toggleTag(tag)}
                />
              ))}
            </Stack>
            {(activeTags.length > 0 || search) && (
              <Button
                size="small"
                onClick={() => {
                  setActiveTags([])
                  setSearch("")
                }}
                sx={{ textTransform: "none" }}
              >
                Clear
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              {filtered.length} of {consultants.length}
            </Typography>
          </Stack>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: "none", overflow: "auto" }}
          >
            <Table aria-label="consultants" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 102,
                      bgcolor: "background.paper",
                      boxShadow: `inset -1px 0 0 0 ${appTheme.palette.divider}`,
                    }}
                  >
                    Consultant
                  </TableCell>
                  <TableCell align="left">Tags</TableCell>
                  <TableCell align="left">Accepting leads</TableCell>
                  <TableCell align="left">Shifts</TableCell>
                  {isLMSAdmin && <TableCell align="left">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((consultant, index) => (
                  <TableRow
                    key={consultant.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      bgcolor: consultant.isAvailable
                        ? appTheme.palette.eTypes.sand15
                        : "#FEECEB",
                    }}
                    hover={consultant.isAvailable}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 101,
                        bgcolor: consultant.isAvailable
                          ? appTheme.palette.eTypes.sand15
                          : "#FEECEB",
                        boxShadow: `inset -1px 0 0 0 ${appTheme.palette.divider}`,
                      }}
                    >
                      <ConsultantCell
                        consultant={consultant}
                        index={index}
                        key={consultant.id}
                        isOnline={onlineConsultansIds.includes(consultant.id)}
                      />
                    </TableCell>
                    <TagsCell
                      tags={
                        (consultant as unknown as { tags?: string[] }).tags ??
                        []
                      }
                      options={allTags}
                      onChange={(tags) => handleTagsChange(consultant.id, tags)}
                    />
                    <TableCell align="left">
                      <Tooltip title="Manual pause on top of the schedule. A consultant gets leads only when this is on and they're on a shift right now.">
                        <Switch
                          checked={consultant.isAvailable}
                          onChange={() =>
                            handleAvailableChange({
                              consultantId: consultant.id,
                              newIsAvailable: !consultant.isAvailable,
                            })
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {ShiftCell(consultant.shifts)}
                        <Tooltip title="Manage shifts">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/sales-management/shifts/${consultant.id}`,
                              )
                            }
                          >
                            <CalendarMonthOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    {isLMSAdmin && (
                      <ActionsCell
                        consultantId={consultant.id}
                        dispatch={dispatch}
                      />
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Dialog
        open={openConsultantInsertForm}
        onClose={() => setOpenConsultantInsertForm(false)}
        maxWidth="md"
      >
        <Box height="24px" />
        <DialogTitle>Add Consultant</DialogTitle>
        <Box height="2px" />
        <DialogContent>
          <ContactForm
            onCreate={onCreateConsultant}
            onClose={() => setOpenConsultantInsertForm(false)}
          />
        </DialogContent>
      </Dialog>
      <TagManagerDialog
        open={manageTagsOpen}
        onClose={() => {
          setManageTagsOpen(false)
          dispatch(fetchSalesTeams())
          loadCatalog()
        }}
      />
    </Box>
  )
}

const ActionsCell = (props: {
  consultantId: string
  dispatch: AppDispatch
}) => {
  const { consultantId, dispatch } = props

  return (
    <TableCell component="th" scope="row">
      <OptionsButton2
        buttonModel={{
          type: "icon",
          props: {
            sx: { display: "flex" },
            size: "small",
            children: (
              <SvgIcon
                component={appIcons.dotsHorizontal}
                inheritViewBox
                sx={{ color: "primary.main" }}
              />
            ),
          },
        }}
        items={[
          {
            type: "menu-item",
            props: {
              key: "archive-consultant",
              children: (
                <IconTextMenuItem text="Archive" icon={appIcons.logIn02} />
              ),
              onClick: () =>
                dispatch(archiveConsultant({ consultantId: consultantId })),
            },
          },
        ]}
      />
    </TableCell>
  )
}
